import {inject, Injectable, signal} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap} from 'rxjs/operators';
import {ToastService} from '@frontend/toast';
import {ProjectStepStateService} from '../../pages/customer/project-sequence/project-step-state.service';
import {RichTextFieldMap} from '../model/rich-text-editor.model';

export interface RichTextEditorHandler {
  onSave(key: string, content: string): Observable<unknown>;
  onContentChange(key: string, content: string): void;
  onContentReset(key: string, content: string): void;
}

export interface RichTextSection {
  key: string;
  content: string;
  tooltipKey: string;
  hasChanges: boolean;
}

export interface RichTextSectionConfig<TRequest, TResponse = TRequest> {
  sections: RichTextSection[];
  fieldMap: Record<string, keyof TRequest>;
  saveFunction: (projectId: number, payload: TRequest) => Observable<TResponse>;
  loadFunction: (projectId: number) => Observable<TResponse>;
}

@Injectable()
export class ProjectStepRichTextService<
  TRequest extends object,
  TResponse extends object = TRequest,
> implements RichTextEditorHandler {
  private readonly toast = inject(ToastService);
  private readonly stepState = inject(ProjectStepStateService);

  private readonly sections = signal<RichTextSection[]>([]);
  private readonly savingStates = signal<Map<string, boolean>>(new Map());
  private config: RichTextSectionConfig<TRequest, TResponse> | null = null;
  private projectId = 0;

  initialize(
    projectId: number,
    config: RichTextSectionConfig<TRequest, TResponse>,
    preloadedData?: TResponse,
  ): void {
    this.projectId = projectId;
    this.config = config;
    this.sections.set([...config.sections]);

    if (preloadedData) {
      this.initializeWithPreloadedData(preloadedData);
    } else {
      this.loadData().subscribe();
    }
  }

  getSections() {
    return this.sections;
  }

  private initializeWithPreloadedData(data: TResponse): void {
    this.sections.update((sections) =>
      sections.map((s) => ({
        ...s,
        content: (data[this.keyToResponseField(s.key)] as string) ?? '',
        hasChanges: false,
      })),
    );
  }

  private loadData(): Observable<void> {
    if (!this.config || !this.projectId) {
      return of(void 0);
    }

    return this.config.loadFunction(this.projectId).pipe(
      map((data) => {
        this.sections.update((sections) =>
          sections.map((s) => ({
            ...s,
            content: (data[this.keyToResponseField(s.key)] as string) ?? '',
            hasChanges: false,
          })),
        );
      }),
      catchError(() => of(void 0)),
    );
  }

  onContentChange(key: string, content: string): void {
    this.sections.update((sections) =>
      sections.map((s) => (s.key === key ? {...s, content, hasChanges: true} : s)),
    );
  }

  onContentReset(key: string, content: string): void {
    this.sections.update((sections) =>
      sections.map((s) => (s.key === key ? {...s, content, hasChanges: false} : s)),
    );
  }

  onSave(key: string, content: string): Observable<TResponse> {
    if (!this.config) throw new Error('Service not initialized');
    if (this.isSaving(key)) return new Observable((subscriber) => subscriber.complete());

    this.setSaving(key, true);
    const field = this.keyToField(key);
    const actual = content || (this.sections().find((s) => s.key === key)?.content ?? '');
    const body = {[field]: actual} as TRequest;

    return this.config.saveFunction(this.projectId, body).pipe(
      switchMap((response) => this.handleSaveSuccess(key, response, actual)),
      finalize(() => this.setSaving(key, false)),
    );
  }

  private handleSaveSuccess(
    key: string,
    response: TResponse,
    content: string,
  ): Observable<TResponse> {
    this.updateAfterSave(key, content);
    return of(response);
  }

  private updateAfterSave(key: string, content: string): void {
    this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    this.stepState.loadStepStatuses(this.projectId);
    this.sections.update((sections) =>
      sections.map((s) => (s.key === key ? {...s, content, hasChanges: false} : s)),
    );
  }

  isSaving(key: string): boolean {
    return this.savingStates().get(key) ?? false;
  }

  private setSaving(key: string, saving: boolean): void {
    this.savingStates.update((states) => {
      const m = new Map(states);
      m.set(key, saving);
      return m;
    });
  }

  private keyToField(key: string): keyof TRequest {
    if (!this.config) throw new Error('Service not initialized');
    return this.config.fieldMap[key];
  }

  private keyToResponseField(key: string): keyof TResponse {
    if (!this.config) throw new Error('Service not initialized');
    return this.config.fieldMap[key] as unknown as keyof TResponse;
  }

  hasContent(sectionKey: string): boolean {
    const section = this.sections().find((section) => section.key === sectionKey);
    const content = section?.content ?? '';
    const trimmedContent = content.trim();
    return (
      trimmedContent !== '' && trimmedContent !== '<p></p>' && trimmedContent !== '<p><br></p>'
    );
  }

  getCompletedSectionsCount(): number {
    const sections = this.sections();
    const completedSections = sections.filter((section) => this.hasContent(section.key));
    return completedSections.length;
  }

  getTotalSectionsCount(): number {
    return this.sections().length;
  }
}

export function calculateRichTextSectionStatus(
  data: any,
  sections: RichTextSection[],
  fieldMap: RichTextFieldMap,
): number {
  if (!data) return 0;

  return sections.filter((section) => {
    const fieldName = fieldMap[section.key];
    if (!fieldName) return false;

    const content = data[fieldName] ?? '';
    const trimmedContent = content.trim();
    return (
      trimmedContent !== '' && trimmedContent !== '<p></p>' && trimmedContent !== '<p><br></p>'
    );
  }).length;
}
